import { useState, useEffect, FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";

type Lang = "en" | "ar";

function useLang() {
  const [lang] = useState<Lang>(() => (localStorage.getItem("diyafa_lang") as Lang) ?? "en");
  const isAr = lang === "ar";
  const t = (en: string, ar: string) => (isAr ? ar : en);
  return { isAr, t };
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const { isAr, t } = useLang();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [invalidLink, setInvalidLink] = useState(false);

  useEffect(() => {
    // Supabase sends the recovery token in the URL hash
    // onAuthStateChange fires with event "PASSWORD_RECOVERY" when the hash is processed
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setSessionReady(true);
      }
    });

    // Also check if there's already a session (user came back to the page)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setSessionReady(true);
      } else {
        // Give it 2 seconds for the hash to be processed
        setTimeout(() => {
          supabase.auth.getSession().then(({ data: { session: s } }) => {
            if (!s) setInvalidLink(true);
          });
        }, 2000);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    if (!password || !confirmPassword) {
      setError(t("Please fill in all fields.", "يرجى ملء جميع الحقول."));
      return;
    }
    if (password.length < 6) {
      setError(t("Password must be at least 6 characters.", "يجب أن تكون كلمة المرور 6 أحرف على الأقل."));
      return;
    }
    if (password !== confirmPassword) {
      setError(t("Passwords do not match.", "كلمتا المرور غير متطابقتين."));
      return;
    }

    setLoading(true);
    const { error: err } = await supabase.auth.updateUser({ password });
    setLoading(false);

    if (err) {
      setError(err.message);
      return;
    }

    setSuccess(true);
    setTimeout(() => navigate("/dashboard"), 3000);
  };

  const inputClass = `w-full px-4 py-3 rounded-xl text-sm border border-line-strong bg-white outline-none focus:border-gold transition-colors text-ink placeholder-[#B0A898] ${isAr ? "font-tajawal text-right" : ""}`;

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4" dir={isAr ? "rtl" : "ltr"}>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-gold/8" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-gold/5" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="inline-flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-ink flex items-center justify-center">
              <i className="ri-door-open-line text-white text-lg"></i>
            </div>
            <span className={`font-bold text-xl text-ink ${isAr ? "font-tajawal" : "font-inter"}`}>
              ضيافة Diyafa
            </span>
          </Link>
        </div>

        <div className="bg-white rounded-3xl overflow-hidden">
          <div className="h-1.5 w-full bg-gradient-to-r from-gold via-[#E8C060] to-gold-dark" />

          <div className="p-8">
            {success ? (
              <div className="text-center py-6">
                <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-green-500 text-3xl"></i>
                </div>
                <h2 className={`font-bold text-xl text-ink mb-2 ${isAr ? "font-tajawal" : "font-inter"}`}>
                  {t("Password Updated!", "تم تحديث كلمة المرور!")}
                </h2>
                <p className={`text-sm text-muted mb-6 leading-relaxed ${isAr ? "font-tajawal" : ""}`}>
                  {t(
                    "Your password has been reset successfully. Redirecting you to the dashboard...",
                    "تم إعادة تعيين كلمة المرور بنجاح. جارٍ توجيهك إلى لوحة التحكم..."
                  )}
                </p>
                <div className="flex items-center justify-center gap-2 text-gold">
                  <i className="ri-loader-4-line animate-spin"></i>
                  <span className={`text-sm ${isAr ? "font-tajawal" : ""}`}>{t("Redirecting...", "جارٍ التوجيه...")}</span>
                </div>
              </div>
            ) : invalidLink ? (
              <div className="text-center py-6">
                <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-error-warning-line text-red-500 text-3xl"></i>
                </div>
                <h2 className={`font-bold text-xl text-ink mb-2 ${isAr ? "font-tajawal" : "font-inter"}`}>
                  {t("Invalid or Expired Link", "رابط غير صالح أو منتهي الصلاحية")}
                </h2>
                <p className={`text-sm text-muted mb-6 leading-relaxed ${isAr ? "font-tajawal" : ""}`}>
                  {t(
                    "This password reset link is invalid or has expired. Please request a new one.",
                    "رابط إعادة تعيين كلمة المرور غير صالح أو منتهي الصلاحية. يرجى طلب رابط جديد."
                  )}
                </p>
                <Link
                  to="/"
                  className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gold text-ink font-semibold text-sm hover:bg-gold-dark transition-colors whitespace-nowrap cursor-pointer ${isAr ? "font-tajawal" : ""}`}
                >
                  {t("Back to Home", "العودة للرئيسية")}
                </Link>
              </div>
            ) : !sessionReady ? (
              <div className="text-center py-10">
                <div className="w-12 h-12 rounded-xl bg-ink flex items-center justify-center mx-auto mb-4">
                  <i className="ri-loader-4-line animate-spin text-gold text-xl"></i>
                </div>
                <p className={`text-sm text-muted ${isAr ? "font-tajawal" : ""}`}>
                  {t("Verifying your reset link...", "جارٍ التحقق من رابط الإعادة...")}
                </p>
              </div>
            ) : (
              <>
                <div className="mb-7">
                  <div className="w-12 h-12 rounded-xl bg-gold/15 flex items-center justify-center mb-4">
                    <i className="ri-lock-password-line text-gold text-xl"></i>
                  </div>
                  <h2 className={`font-bold text-2xl text-ink ${isAr ? "font-tajawal" : "font-inter"}`}>
                    {t("Set New Password", "تعيين كلمة مرور جديدة")}
                  </h2>
                  <p className={`text-sm text-muted mt-1 ${isAr ? "font-tajawal" : ""}`}>
                    {t("Choose a strong password for your account", "اختر كلمة مرور قوية لحسابك")}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium text-ink-soft mb-1.5 ${isAr ? "font-tajawal" : ""}`}>
                      {t("New Password", "كلمة المرور الجديدة")}
                    </label>
                    <div className="relative">
                      <input
                        type={showPass ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={t("Min. 6 characters", "6 أحرف على الأقل")}
                        className={`${inputClass} ${isAr ? "pl-10" : "pr-10"}`}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPass(!showPass)}
                        className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-[#B0A898] hover:text-ink-soft cursor-pointer transition-colors ${isAr ? "left-3" : "right-3"}`}
                      >
                        <i className={`text-sm ${showPass ? "ri-eye-off-line" : "ri-eye-line"}`}></i>
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium text-ink-soft mb-1.5 ${isAr ? "font-tajawal" : ""}`}>
                      {t("Confirm New Password", "تأكيد كلمة المرور الجديدة")}
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirm ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder={t("Repeat your new password", "أعد كتابة كلمة المرور الجديدة")}
                        className={`${inputClass} ${isAr ? "pl-10" : "pr-10"}`}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirm(!showConfirm)}
                        className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-[#B0A898] hover:text-ink-soft cursor-pointer transition-colors ${isAr ? "left-3" : "right-3"}`}
                      >
                        <i className={`text-sm ${showConfirm ? "ri-eye-off-line" : "ri-eye-line"}`}></i>
                      </button>
                    </div>
                  </div>

                  {/* Password strength indicator */}
                  {password.length > 0 && (
                    <div className="space-y-1">
                      <div className="flex gap-1">
                        {[1, 2, 3, 4].map((level) => (
                          <div
                            key={level}
                            className={`flex-1 h-1 rounded-full transition-colors ${
                              password.length >= level * 3
                                ? password.length >= 12 ? "bg-green-500" : password.length >= 8 ? "bg-gold" : "bg-red-400"
                                : "bg-line-strong"
                            }`}
                          />
                        ))}
                      </div>
                      <p className={`text-[10px] text-muted ${isAr ? "font-tajawal" : ""}`}>
                        {password.length < 6
                          ? t("Too short", "قصيرة جداً")
                          : password.length < 8
                          ? t("Weak", "ضعيفة")
                          : password.length < 12
                          ? t("Good", "جيدة")
                          : t("Strong", "قوية")}
                      </p>
                    </div>
                  )}

                  {error && (
                    <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-100">
                      <i className="ri-error-warning-line text-red-400 text-sm flex-shrink-0"></i>
                      <p className={`text-xs text-red-500 ${isAr ? "font-tajawal" : ""}`}>{error}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full py-3.5 rounded-xl font-semibold text-ink bg-gold hover:bg-gold-dark cursor-pointer transition-all disabled:opacity-70 whitespace-nowrap mt-1 ${isAr ? "font-tajawal" : ""}`}
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin text-base"></i>
                        {t("Updating password...", "جارٍ تحديث كلمة المرور...")}
                      </span>
                    ) : (
                      t("Update Password", "تحديث كلمة المرور")
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
